// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { observer } from 'mobx-react';
import get from 'lodash/get';
import classNames from 'classnames/bind';
import cx from 'classnames';
import Asset from '@shared/models/asset';
import { ASSET_TYPE_LABELS, ASSET_CONSTRUCTION_TYPE_LABELS, TOWER_FIELDS } from '@shared/constants/asset';
import Flex from '@shared/components/Flex';
import Ellipsis from '@shared/components/Ellipsis';
import Label from '@shared/components/Label';
import Image from '@shared/components/Image';

export interface AssetViewingProps {
  className?: string;
  data: Asset;
}

interface AssetField {
  label: string;
  render?: (asset: Asset) => React.ReactNode;
  field?: string;
  rootValueClass?: string;
  withTooltip?: boolean;
}

interface AssetSection {
  heading: string;
  fields: Array<AssetField>;
}

@observer
class AssetViewing extends React.Component<AssetViewingProps> {
  
  private get assetSections(): Array<AssetSection> {
    const classes = withStyles.getClasses(this.props);

    return [
      {
        heading: 'General information',
        fields: [
          {
            label: 'Construction photo',
            withTooltip: false,
            render: (asset) => (
              <Image
                src={asset.assetImageUrl}
                classes={{
                  root: classes.assetImgRoot
                }}
              />
            )
          },
          {
            label: 'Tower type',
            withTooltip: false,
            render: (asset) => ASSET_TYPE_LABELS[asset.type]
          },
          {
            label: 'Type',
            withTooltip: false,
            render: (asset) => ASSET_CONSTRUCTION_TYPE_LABELS[asset.construction?.type]
          },
          { label: 'Serial number', field: TOWER_FIELDS.serialNumber },
          { 
            label: 'Notes', 
            field: TOWER_FIELDS.note,
            rootValueClass: classes.note
          },
        ]
      },
      {
        heading: 'Address',
        fields: [
          { label: 'Street number and name', field: `${TOWER_FIELDS.address}.${TOWER_FIELDS.address.fields.addressLine}` },
          { label: 'City', field: `${TOWER_FIELDS.address.name}.${TOWER_FIELDS.address.fields.city}` },
          { label: 'State', field: `${TOWER_FIELDS.address.name}.${TOWER_FIELDS.address.fields.state}` },
          { label: 'Zip code', field: `${TOWER_FIELDS.address.name}.${TOWER_FIELDS.address.fields.zipCode}` },
          { label: 'Country', field: `${TOWER_FIELDS.address.name}.${TOWER_FIELDS.address.fields.country}` },
          { label: 'Height, m', field: `${TOWER_FIELDS.construction.name}.${TOWER_FIELDS.construction.fields.height}` },
          {
            label: 'Lat & long',
            withTooltip: false,
            render: (asset) => (
              <Flex container>
                <Ellipsis text={asset.coordinates?.lat} classes={{ root: classes.latLon }} />
                <Ellipsis text={asset.coordinates?.lon} classes={{ root: classes.latLon }} />
              </Flex>
            )
          }
        ]
      }
    ];
  }

  render() {
    const { data } = this.props;
    const classes = withStyles.getClasses(this.props);

    return (
      <Flex autoWidth={false} justifyContent="space-between" className={classes.root}>
        {this.assetSections.map((section) => (
          <div className={classes.section} key={section.heading}>
            <h6 className={classes.title}>{section.heading}</h6>

            {section.fields.map(({ label, field = '', withTooltip = true, rootValueClass, render }) => (
              <Flex
                alignItems="center"
                key={label}
                className={classes.field}
              >
                <Label withMarginTop={false} classes={{ root: classes.label }} text={label} />
                <Ellipsis
                  withTooltip={withTooltip}
                  classes={{ root: cx(classes.value, rootValueClass) }}
                  text={render ? render(data) : get(data, field, '')}
                />
              </Flex>
            ))}
          </div>
        ))}
      </Flex>
    );
  }
}

const AssetViewingStyled = withStyles(
  AssetViewing, 
  (theme, props) => ({
    "root": {},
    "title": {
      fontSize: 17,
      color: theme.palette.colors.black,
      marginBottom: theme.spacing(4)
    },
    "section": {
      borderRadius: '4px',
      width: 'calc(50% - 4px)',
      backgroundColor: theme.palette.colors.white,
      padding: theme.spacing(5)
    },
    "field": {
      marginBottom: theme.spacing(2),
      flexWrap: 'nowrap',
      width: '100%',

      '&:last-of-type': {
        marginBottom: 0
      }
    },
    "label": {
      width: '50%',
      marginBottom: 0
    },
    "value": {
      width: '50%',
      color: theme.palette.colors.black,
      fontSize: 15
    },
    "note": {
      maxHeight: 170,
      overflow: 'auto',
      wordWrap: 'break-word',
      whiteSpace: 'normal'
    },
    "latLon": {
      marginRight: theme.spacing(6)
    },
    "assetImgRoot": {
      height: 66,
      width: 'auto',
      objectFit: 'contain',
    }
  })
);

export default AssetViewingStyled;